import React, { useState, useEffect, useCallback } from 'react';
import { uploadAPI } from '../api';

const UploadPage = ({ token }) => {
  const [request, setRequest] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadComplete, setUploadComplete] = useState({});
  const [documentMap, setDocumentMap] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const loadRequestData = useCallback(async () => {
    try {
      const data = await uploadAPI.getRequestByToken(token);
      setRequest(data);

      // Initialize upload status tracking
      const progress = {};
      const complete = {};
      const docMap = {};
      data.documents.forEach(doc => {
        progress[doc.name] = 0;
        complete[doc.name] = doc.status === 'uploaded';
        docMap[doc.name] = doc.id;
      });
      setUploadProgress(progress);
      setUploadComplete(complete);
      setDocumentMap(docMap);
    } catch (err) {
      setError('Failed to load request details. Please check the link and try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadRequestData();
  }, [loadRequestData]);

  const handleFileSelect = (file, documentName) => {
    handleFileUpload(file, documentName);
  };

  const handleFileUpload = async (file, documentName) => {
    // Validate file size (10MB limit)
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      setError(`File too large (${sizeMB}MB). Maximum: 10MB. Try compressing the image or PDF.`);
      return;
    }

    // Validate file type
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(`File type not supported (${file.type}). Upload JPEG, PNG, or PDF only.`);
      return;
    }

    setUploading(prev => ({ ...prev, [documentName]: true }));
    setError('');

    try {
      const documentId = documentMap[documentName];
      if (!documentId) {
        throw new Error(`Document ID not found for ${documentName}`);
      }

      await uploadAPI.uploadFile(token, documentId, file, (progress) => {
        setUploadProgress(prev => ({ ...prev, [documentName]: progress }));
      });

      setUploadComplete(prev => ({ ...prev, [documentName]: true }));
      setUploadProgress(prev => ({ ...prev, [documentName]: 100 }));

      // Reload request data to get updated status
      await loadRequestData();
    } catch (err) {
      setError(`Failed to upload ${documentName}. Please try again.`);
      setUploadProgress(prev => ({ ...prev, [documentName]: 0 }));
    } finally {
      setUploading(prev => ({ ...prev, [documentName]: false }));
    }
  };

  const triggerFileInput = (documentName, inputId) => {
    const input = document.getElementById(inputId);
    if (input) {
      input.click();
    }
  };

  const capturePhoto = (documentName) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use back camera
    input.onchange = (e) => {
      if (e.target.files[0]) {
        handleFileSelect(e.target.files[0], documentName);
      }
    };
    input.click();
  };

  const selectFromGallery = (documentName) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';
    input.multiple = false;
    input.onchange = (e) => {
      if (e.target.files[0]) {
        handleFileSelect(e.target.files[0], documentName);
      }
    };
    input.click();
  };

  const getStatusIcon = (document) => {
    if (uploadComplete[document.name]) {
      return (
        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    } else if (uploading[document.name]) {
      return (
        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        </div>
      );
    } else {
      return (
        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </div>
      );
    }
  };

  const allDocumentsUploaded = () => {
    return request?.documents.every(doc => uploadComplete[doc.name]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading request details...</p>
        </div>
      </div>
    );
  }

  if (error && !request) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Request Not Found</h2>
          <p className="text-gray-600 text-sm mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="touch-manipulation bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm safe-area pt-6 pb-4">
        <div className="px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">DoqBridge</h1>
              <p className="text-sm text-gray-600">Document Upload</p>
              {request?.advisor_name && (
                <p className="text-xs text-gray-500 mt-1">{request.advisor_name} has requested these documents</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Client:</p>
              <p className="font-medium text-gray-900">{request?.client_name}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Overview */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Upload Progress</span>
          <span className="text-sm text-gray-600">
            {request?.documents.filter(doc => uploadComplete[doc.name]).length} of {request?.documents.length}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{
              width: `${((request?.documents.filter(doc => uploadComplete[doc.name]).length || 0) / (request?.documents.length || 1)) * 100}%`
            }}
          ></div>
        </div>
      </div>

      {/* Document List */}
      <main className="p-4 space-y-4 safe-area pb-8">
        {request?.documents.map((document, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  {getStatusIcon(document)}
                  <div>
                    <h3 className="font-medium text-gray-900">{document.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {uploadComplete[document.name] ? 'Uploaded successfully' : 'Required document'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Upload Progress */}
              {uploading[document.name] && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Uploading...</span>
                    <span>{uploadProgress[document.name]}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1">
                    <div
                      className="bg-blue-600 h-1 rounded-full transition-all duration-200"
                      style={{ width: `${uploadProgress[document.name]}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Upload Actions */}
              {!uploadComplete[document.name] && !uploading[document.name] && (
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <button
                    onClick={() => capturePhoto(document.name)}
                    className="touch-manipulation bg-blue-50 hover:bg-blue-100 text-blue-700 py-3 px-3 rounded-lg text-sm font-medium border border-blue-200 flex flex-col items-center space-y-1"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Camera</span>
                  </button>
                  
                  <button
                    onClick={() => selectFromGallery(document.name)}
                    className="touch-manipulation bg-green-50 hover:bg-green-100 text-green-700 py-3 px-3 rounded-lg text-sm font-medium border border-green-200 flex flex-col items-center space-y-1"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Gallery</span>
                  </button>
                  
                  <button
                    onClick={() => triggerFileInput(document.name, `file-${index}`)}
                    className="touch-manipulation bg-purple-50 hover:bg-purple-100 text-purple-700 py-3 px-3 rounded-lg text-sm font-medium border border-purple-200 flex flex-col items-center space-y-1"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Files</span>
                  </button>

                  <input
                    id={`file-${index}`}
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleFileSelect(e.target.files[0], document.name);
                      }
                    }}
                    className="hidden"
                  />
                </div>
              )}

              {/* Re-upload Option */}
              {uploadComplete[document.name] && (
                <div className="mt-4">
                  <button
                    onClick={() => selectFromGallery(document.name)}
                    className="touch-manipulation w-full bg-gray-50 hover:bg-gray-100 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium border border-gray-200"
                  >
                    Replace Document
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Completion Message */}
        {allDocumentsUploaded() && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-green-800 mb-2">Upload Complete!</h3>
            <p className="text-green-700 text-sm mb-4">
              All documents have been uploaded successfully. You will receive a confirmation on WhatsApp.
            </p>
            <div className="bg-white border border-green-200 rounded-lg p-3">
              <p className="text-green-800 text-xs">
                ✅ Safe to close this page<br />
                Your advisor has been notified
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default UploadPage;